import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Tooltip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  FormControlLabel,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Collapse,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BuildIcon from "@mui/icons-material/Build";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { createAgent, updateAgent, listIndexes, listTools, listKeys } from "../../api/agentApi";
import { useLocation, useNavigate } from "react-router-dom";
import AutocompleteInput from "./Options/AutocompleteInput.tsx";
import AutocompleteInputName from "./Options/AutocompleteInputName.tsx";

import {
  roleOptions,
  intentOptions,
  toolSuggestions,
  llmOptions,
  createSteps,
  editSteps,
} from "./Constants.ts";
import { useUser } from "../UserContext";

interface Props {
  onAgentCreated?: (agentId: string) => void;
}

const StepperWizard: React.FC<Props> = (props) => {
  const [availableKeys, setAvailableKeys] = useState<any[]>([]);
  const navigate = useNavigate();
  // Fetch available keys for key dropdowns
  useEffect(() => {
    async function fetchKeys() {
      try {
        const res = await listKeys();
        setAvailableKeys(res.keys || []);
      } catch (e) {
        setAvailableKeys([]);
      }
    }
    fetchKeys();
  }, []);
  const { user } = useUser();
  const location = useLocation();
  const editingAgentCard = location.state?.agentCard;
  // Determine if this is an update (edit) or create
  // If editing, get agentCard from location.state
  const isEdit = !!editingAgentCard;
  const steps = isEdit ? editSteps : createSteps;
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<boolean[]>(
    new Array(steps.length).fill(false)
  );

  const currentStepConfig = steps[activeStep];
  
  
  // Check if user is non-admin
  const isNonAdmin = user?.role !== "admin";
  
  // If non-admin and editing, skip to step 5 (review step)
  useEffect(() => {
    if (isEdit && isNonAdmin) {
      setActiveStep(4); // Step 5 is index 4
      // Mark all previous steps as completed
      setCompletedSteps([true, true, true, true, false]);
    }
  }, [isEdit, isNonAdmin]);

  const handleNext = () => {
    if (activeStep < steps.length - 1 && isCurrentStepComplete()) {
      const newCompletedSteps = [...completedSteps];
      newCompletedSteps[activeStep] = true;
      setCompletedSteps(newCompletedSteps);

      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    // Prevent going back if non-admin in edit mode
    if (isEdit && isNonAdmin) {
      return;
    }
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  // --------------------------------------------------

  const [form, setForm] = useState(() => {
    if (editingAgentCard) {
      return {
        role: editingAgentCard.role || "",
        name: editingAgentCard.name || "",
        display_name: editingAgentCard.display_name || "",
        description: editingAgentCard.description || "",
        tools: editingAgentCard.tools || [],
        llm: editingAgentCard.llm || "openai",
        temperature: editingAgentCard.llm_settings?.temperature ?? 0,
        max_tokens: editingAgentCard.llm_settings?.max_tokens ?? 4096,
        status: editingAgentCard.status || "Creating",
      };
    }
    return {
      role: "",
      name: "",
      display_name: "",
      description: "",
      tools: [],
      llm: "openai",
      temperature: 0,
      max_tokens: 4096,
      status: "Creating",
    };
  });

  // Check if current step is complete
  const isCurrentStepComplete = (): boolean => {
    switch (activeStep) {
      case 0:
        return !!form.role;
      case 1:
        return !!form.name && !!form.description;
      case 2:
        // Step 2: Tools selection - tools must have a selected key if key is required
        if (selectedTools.length === 0) return true; // Allow skipping if no tools selected
        return selectedTools.every((tool: any) => {
          if (Array.isArray(tool.keys_schema) && tool.keys_schema.length > 0) {
            return tool.selectedKey !== null && tool.selectedKey !== undefined;
          }
          return true;
        });
      case 3:
      case 4:
        return true;
      default:
        return false;
    }
  };

  // Also prepopulate selectedTools if editing
  const [selectedTools, setSelectedTools] = useState(() =>
    Array.isArray(editingAgentCard?.tools)
      ? editingAgentCard.tools.map((t: any) => ({
          ...t,
          paramValues: {},
          // Ensure params is always an array for editing
          params: t.parameters
            ? t.parameters.map((p: any) => ({
                name: p.name || "",
                type: p.type || "string",
                description: p.description || "",
              }))
            : t.params || [],
        }))
      : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customTool, setCustomTool] = useState<any>({
    name: "",
    description: "",
    params: [],
  });
  const [manualMode, setManualMode] = useState(false);
  const [manualJsonValue, setManualJsonValue] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [success, setSuccess] = useState("");
  
  // Tool catalog UI state
  const [showCatalog, setShowCatalog] = useState(true); // Toggle catalog section visibility
  const [toolSearchQuery, setToolSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [toolDetailOpen, setToolDetailOpen] = useState(false);
  const [selectedToolDetail, setSelectedToolDetail] = useState<any>(null);

  const [docIndexes, setDocIndexes] = useState([]);
  const [codeIndexes, setCodeIndexes] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(null);
  const [selectedCodeIndex, setSelectedCodeIndex] = useState(null);
  const [catalogTools, setCatalogTools] = useState<any[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [toolsHydrated, setToolsHydrated] = useState(false);

  useEffect(() => {
    async function fetchIndexes() {
      try {
        const docRes = await listIndexes("doc");
        setDocIndexes(docRes.indexes || []);
        const codeRes = await listIndexes("code");
        setCodeIndexes(codeRes.indexes || []);
      } catch (e) {
        // ignore errors for now
      }
    }
    fetchIndexes();
  }, []);

  // Fetch tools from catalog
  useEffect(() => {
    async function fetchTools() {
      setLoadingTools(true);
      try {
        // Fetch all public tools, then filter for published on frontend
        const toolsRes = await listTools();
        
        // Filter to only show published tools (production-ready)
        const productionTools = toolsRes.filter((tool: any) => 
          tool.status === 'published'
        );

        
        // Transform catalog tools to match the expected format
        const transformedTools = productionTools.map((tool: any) => {
          // Handle new JSON Schema format for parameters 
          let params = [];  
          if (tool.parameters && typeof tool.parameters === 'object') {
            if (Array.isArray(tool.parameters)) {
              // Old array format (backward compatibility)
              params = tool.parameters.map((p: any) => ({
                name: p.name || "",
                type: p.type || "string",
                description: p.description || "",
                required: p.required || false,
                default: p.default,
              }));
            } else if (tool.parameters.properties) {
              // New JSON Schema object format
              const required = tool.parameters.required || [];
              params = Object.entries(tool.parameters.properties).map(([name, prop]: [string, any]) => ({
                name: name,
                type: prop.type || "string",
                description: prop.description || "",
                required: required.includes(name),
                default: prop.default,
              }));
            }
          }
          // Pass through keys_schema for key selection dropdown
          return {
            name: tool.name,
            display_name: tool.display_name || tool.name,
            description: tool.description,
            params: params,
            invocation: tool.invocation_config || { type: tool.invocation_type || "python_function" },
            category: tool.category,
            tool_id: tool.id, // Store the catalog tool ID
            version: tool.version,
            keys_schema: tool.keys_schema || [],
          };
        });
        setCatalogTools(transformedTools);
      } catch (e) {
        console.error("Failed to fetch tools from catalog:", e);
      } finally {
        setLoadingTools(false);
      }
    }
    fetchTools();
  }, []);

  useEffect(() => {
    if (!isEdit || toolsHydrated || catalogTools.length === 0) return;

    setSelectedTools((prev: any[]) =>
      prev.map((tool: any) => {
        const catalogMatch = catalogTools.find(
          (catalogTool: any) =>
            (tool.tool_id && catalogTool.tool_id === tool.tool_id) ||
            (!tool.tool_id && catalogTool.name === tool.name)
        );

        const hydratedKeysSchema =
          tool.keys_schema && tool.keys_schema.length > 0
            ? tool.keys_schema
            : catalogMatch?.keys_schema || [];

        let hydratedSelectedKey = tool.selectedKey;
        if (!hydratedSelectedKey && Array.isArray(tool.required_keys) && tool.required_keys.length > 0) {
          // Assume single key for now
          const requiredKeyEntry = tool.required_keys[0];
          const requiredKeyName = requiredKeyEntry && Object.keys(requiredKeyEntry)[0];
          const requiredKeyValue = requiredKeyName ? requiredKeyEntry[requiredKeyName] : null;
          hydratedSelectedKey = requiredKeyValue ?? null;
        }

        return {
          ...tool,
          keys_schema: hydratedKeysSchema,
          selectedKey: hydratedSelectedKey,
        };
      })
    );

    setToolsHydrated(true);
  }, [catalogTools, isEdit, toolsHydrated]);
   const formatToolKeyForAgent = (tool: any) => {
    const requiredKeysArr: any[] = [];
    if (tool.tool_id && Array.isArray(tool.keys_schema) && tool.keys_schema.length > 0 && tool.selectedKey) {
      // Assume single key for now
      const keyName = tool.keys_schema[0].name;
      requiredKeysArr.push({ [keyName]: tool.selectedKey });
      return requiredKeysArr;
    }
    return undefined;
  };

  // Prepopulate selectedDocIndex and selectedCodeIndex in Edit mode if rag array is present
  useEffect(() => {
    if (isEdit && editingAgentCard && Array.isArray(editingAgentCard.rag)) {
      const docIdx = editingAgentCard.rag.find((idx:any) => idx.rag_type === "doc");
      const codeIdx = editingAgentCard.rag.find((idx:any) => idx.rag_type === "code");
      if (docIdx && docIdx.id) setSelectedDocIndex(docIdx.id);
      if (codeIdx && codeIdx.id) setSelectedCodeIndex(codeIdx.id);
    }
  }, [isEdit, editingAgentCard]);

  const reviewTools = selectedTools;
 
  // Convert tools to schema format (parameters array, not paramValues)
  const toolsForCard = reviewTools.map((tool: any) => ({
    name: tool.name,
    description: tool.description,
    parameters: (tool.params || []).map((param: any) => ({
      name: param.name,
      type: param.type,
      description: param.description || "",
    })),
    invocation:
      tool.invocation && tool.invocation.type
        ? { type: tool.invocation.type }
        : { type: "python_function" },
    // Include catalog tool metadata if present
    ...(tool.tool_id && { tool_id: tool.tool_id }),
    ...(tool.version && { version: tool.version }),
    required_keys:  (tool.tool_id && Array.isArray(tool.keys_schema) && tool.keys_schema.length > 0 && tool.selectedKey) ?
      formatToolKeyForAgent(tool) : undefined,
  }));
  // Compose llm_settings
  const llm_settings = {
    temperature: form.temperature,
    max_tokens: form.max_tokens,
  };
  // Compose agent card
  const agentCard = {
    name: form.name,
    description: form.description,
    llm: form.llm,
    llm_settings,
    role: form.role,
    tools: toolsForCard,
    // Optionally add inputs/output if you have those steps
    owner: user?.username || "Unknown",
    ...(form.display_name && { display_name: form.display_name }), // Include display_name if provided
  };
  // When agent is created, update status to "created"
  const handleCreateAgent = async () => {
    setLoading(true);
    setError("");
    setSuccessMsg("");
    setSuccess("");
    try {
      let result;
      if (isEdit && editingAgentCard) {
        // Update agent: pass index IDs in payload only if selected
        const updatePayload: any = { ...agentCard };
        if (selectedDocIndex)
          updatePayload.document_index_id = selectedDocIndex;
        if (selectedCodeIndex) updatePayload.code_index_id = selectedCodeIndex;
        result = await updateAgent(editingAgentCard.agent_id, updatePayload);
      } else {
        // Create agent
        result = await createAgent(
          agentCard,
          selectedDocIndex,
          selectedCodeIndex
        );
      }
      if (result.status === "success" || result.status === "started") {
        const kbDetailUrl = `/kb/${result.type === "doc" ? "doc" : "code"}/${
          result.task_id
        }`;
        navigate(`/agent/${result.agent_id}`);
        if (props.onAgentCreated) props.onAgentCreated(result.agent_id);
      } else {
        setError(result.message || "Failed to save agent.");
      }
    } catch (e: any) {
      setError(e.message || "Failed to save agent.");
    } finally {
      setLoading(false);
      
    }
  };
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}></Box>
            {form.role && (
              <Box sx={{ mt: 2, mb: 1, ml: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  Great! You're creating an agent as a <b>{form.role}</b>.
                </Typography>
              </Box>
            )}
            <AutocompleteInput
              options={roleOptions}
              setForm={setForm}
              form={form}
              value={form.role}
              label={"Agent Role"}
              placeholder="Select or type custom value..."
              helperText="Choose from suggestions or enter your own"
            />
          </Box>
        );
      case 1:
        const intentOptionsFromRole = intentOptions[form.role] || [];
        // const options:any = [];
        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role: <b>{form.role}</b>
              </Typography>
            </Box>
            {/* <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}> */}
            <Box sx={{ mr: 2, mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                🗨️ I want to create an agent for...
              </Typography>
            </Box>
            <AutocompleteInputName
              options={intentOptionsFromRole}
              setForm={setForm}
              form={form}
              value={form.name}
              label={"Agent Intent"}
              placeholder="What does your agent do? What problem does it solve?"
            />
            
            {/* Display Name Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Display Name (Optional)
              </Typography>
              <TextField
                label="Display Name"
                value={form.display_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, display_name: e.target.value }))
                }
                fullWidth
                placeholder="A user-friendly name for your agent (e.g., 'Sales Assistant', 'Data Analyzer')"
                helperText="If not provided, the agent intent will be used as the display name"
              />
            </Box>
            
            {/* Agent Description Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Describe your agent
              </Typography>
              <TextField
                label="Agent Description"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                fullWidth
                multiline
                minRows={4}
                placeholder="What does your agent do? What problem does it solve?"
              />
            </Box>
            {/* </Box> */}
          </Box>
        );

      case 2:
        const suggestions = toolSuggestions[form.role] || [];
        const handleToolSelect = (tool: any) => {
          if (!selectedTools.some((t: any) => t.name === tool.name)) {
            setSelectedTools([...selectedTools, { ...tool, paramValues: {}, selectedKey: null }]);
          }
        };
        const handleToolRemove = (name: any) => {
          setSelectedTools(selectedTools.filter((t: any) => t.name !== name));
        };
        const handleKeySelect = (toolIdx: any, keyId: any) => {
          const updated = [...selectedTools];
          updated[toolIdx].selectedKey = keyId;
          setSelectedTools(updated);
        };
        const handleParamChange = (toolIdx: any, param: any, value: any) => {
          const updated = [...selectedTools];
          updated[toolIdx].paramValues[param] = value;
          setSelectedTools(updated);
        };
        const handleAddCustomTool = () => {
          if (customTool.name && customTool.description) {
            setSelectedTools([
              ...selectedTools,
              { ...customTool, paramValues: {}, selectedKey: null },
            ]);
            setCustomTool({ name: "", description: "", params: [] });
          }
        };
        // Tool editing logic for custom tools
        const handleEditTool = (idx: any, field: any, value: any) => {
          setSelectedTools((prev: any) => {
            // Use a new array/object for the tool being edited to avoid React focus issues
            const updated = prev.map((tool: any, i: any) =>
              i === idx ? { ...tool, [field]: value } : tool
            );
            return [...updated];
          });
        };
        const handleEditParam = (
          toolIdx: any,
          paramIdx: any,
          field: any,
          value: any
        ) => {
          setSelectedTools((prev: any) => {
            // Deep copy the tool and params to avoid focus loss and ensure state updates
            const updated = prev.map((tool: any, i: any) => {
              if (i !== toolIdx) return tool;
              const params = tool.params
                ? tool.params.map((param: any, j: any) =>
                    j === paramIdx ? { ...param, [field]: value } : { ...param }
                  )
                : [];
              return { ...tool, params: [...params] };
            });
            return [...updated];
          });
        };
        // Remove parameter
        const handleRemoveParam = (toolIdx: any, paramIdx: any) => {
          setSelectedTools((prev: any) => {
            const updated = prev.map((tool: any, i: any) => {
              if (i !== toolIdx) return tool;
              const params = tool.params
                ? tool.params.filter((_: any, j: any) => j !== paramIdx)
                : [];
              return { ...tool, params: [...params] };
            });
            return [...updated];
          });
        };

        // Add parameter
        const handleAddParam = (toolIdx: any) => {
          setSelectedTools((prev: any) => {
            const updated = prev.map((tool: any, i: any) => {
              if (i !== toolIdx) return tool;
              const params = [
                ...(tool.params || []),
                { name: "", type: "string", description: "" },
              ];
              return { ...tool, params };
            });
            return [...updated];
          });
        };
        // Add missing handler for invocation type
        const handleInvocationTypeChange = (toolIdx: any, value: any) => {
          setSelectedTools((prev: any) => {
            const updated = prev.map((tool: any, i: any) => {
              if (i !== toolIdx) return tool;
              return {
                ...tool,
                invocation: { ...(tool.invocation || {}), type: value },
              };
            });
            return [...updated];
          });
        };

        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role: <b>{form.role}</b>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intent: <b>{form.name}</b>
              </Typography>
            </Box>

            {/* Catalog Tools Section - Collapsible */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="text"
                onClick={() => setShowCatalog(!showCatalog)}
                startIcon={<Inventory2Icon />}
                endIcon={showCatalog ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', mb: showCatalog ? 2 : 0, fontWeight: 500 }}
              >
                {showCatalog ? 'Hide ' : 'Browse '}Catalog Tools
                {!showCatalog && (
                  <Box component="span" sx={{ ml: 1, display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                    {catalogTools.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ({catalogTools.length} available)
                      </Typography>
                    )}
                    {selectedTools.filter((t: any) => t.tool_id).length > 0 && (
                      <Chip 
                        label={`${selectedTools.filter((t: any) => t.tool_id).length} selected`} 
                        size="small" 
                        color="success"
                        sx={{ height: 20, fontSize: '0.75rem' }} 
                      />
                    )}
                  </Box>
                )}
              </Button>
              <Collapse in={showCatalog}>
              
              {loadingTools ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading tools from catalog...
                  </Typography>
                </Box>
              ) : catalogTools.length > 0 ? (
                <Box>
                  {/* Search and Filter Controls */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      placeholder="Search tools..."
                      value={toolSearchQuery}
                      onChange={(e) => setToolSearchQuery(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Autocomplete
                      options={['all', ...Array.from(new Set(catalogTools.map(t => t.category || 'Other')))]}
                      value={selectedCategory}
                      onChange={(_, value) => setSelectedCategory(value || 'all')}
                      renderInput={(params) => (
                        <TextField {...params} label="Category" size="small" />
                      )}
                      sx={{ minWidth: 200 }}
                      disableClearable
                    />
                  </Box>

                  {/* Tool Cards Grid */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2,
                    mb: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                  }}>
                    {catalogTools
                      .filter((tool: any) => {
                        // Filter by search query
                        const matchesSearch = !toolSearchQuery || 
                          tool.name.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                          tool.description.toLowerCase().includes(toolSearchQuery.toLowerCase());
                        
                        // Filter by category
                        const matchesCategory = selectedCategory === 'all' || 
                          (tool.category || 'Other') === selectedCategory;
                        
                        return matchesSearch && matchesCategory;
                      })
                      .map((tool: any) => {
                        const isSelected = selectedTools.some((t: any) => t.tool_id === tool.tool_id);
                        return (
                          <Card 
                            key={tool.tool_id}
                            variant="outlined" 
                            sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              borderWidth: isSelected ? 2 : 1,
                              bgcolor: isSelected ? 'primary.50' : 'background.paper',
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, fontSize: '0.875rem' }}>
                                  {tool.display_name}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    setSelectedToolDetail(tool);
                                    setToolDetailOpen(true);
                                  }}
                                  sx={{ ml: 0.5, mt: -0.5, p: 0.5 }}
                                >
                                  <VisibilityIcon 
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '1.2rem',
                                    color: 'primary.main',
                                    '&:hover': {
                                      bgcolor: 'transparent',
                                      color: 'primary.dark',
                                    },
                                  }}
                                  />
                                </IconButton>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem', lineHeight: 1.3 }}>
                                {tool.description.length > 70 
                                  ? `${tool.description.substring(0, 70)}...` 
                                  : tool.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip 
                                  label={tool.category || 'Other'} 
                                  size="small" 
                                  sx={{ fontSize: '0.625rem', height: 18 }}
                                />
                                {tool.params.length > 0 && (
                                  <Chip 
                                    label={`${tool.params.length} param${tool.params.length !== 1 ? 's' : ''}`} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.625rem', height: 18 }}
                                  />
                                )}
                                <Box sx={{ flex: 1 }} />
                                <Button
                                  size="small"
                                  variant={isSelected ? "contained" : "outlined"}
                                  onClick={() => handleToolSelect(tool)}
                                  disabled={isSelected}
                                  sx={{ 
                                    textTransform: 'none', 
                                    fontSize: '0.7rem',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.25,
                                    height: 22,
                                  }}
                                >
                                  {isSelected ? '✓' : 'Add'}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </Box>
                  
                  {catalogTools.filter((tool: any) => {
                    const matchesSearch = !toolSearchQuery || 
                      tool.name.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                      tool.description.toLowerCase().includes(toolSearchQuery.toLowerCase());
                    const matchesCategory = selectedCategory === 'all' || 
                      (tool.category || 'Other') === selectedCategory;
                    return matchesSearch && matchesCategory;
                  }).length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No tools found matching your search criteria.
                    </Alert>
                  )}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No active tools available in the catalog yet. 
                </Alert>
              )}
              </Collapse>
            </Box>

            {selectedTools.length > 0 && (
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Selected Tools ({selectedTools.length})
                </Typography>
                {selectedTools.map((tool: any, idx: any) => {
                  const isCatalogTool = !!tool.tool_id; // Tools from catalog have tool_id
                  
                  return (
                    <Box
                      key={tool.tool_id || tool.name || idx}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderLeft: isCatalogTool ? "3px solid #1976d2" : "2px solid #eee",
                        bgcolor: isCatalogTool ? "#f5f9ff" : "transparent",
                        borderRadius: 1,
                      }}
                    >
                      {isCatalogTool ? (
                        // Read-only display for catalog tools
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {tool.name}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              px: 1, 
                              py: 0.25, 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              borderRadius: 1,
                              fontSize: '0.65rem'
                            }}>
                              FROM CATALOG
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              v{tool.version}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              onClick={() => handleToolRemove(tool.name)}
                            >
                              Remove
                            </Button>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tool.description}
                          </Typography>
                          
                          
                          {/* Show API Key dropdown only if tool.keys_schema is a non-empty array */}
                          {Array.isArray(tool.keys_schema) && tool.keys_schema.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Autocomplete
                                options={availableKeys}
                                getOptionLabel={(option: any) => `${option.name} (${option.provider})`}
                                value={availableKeys.find((k: any) => k.id === tool.selectedKey) || null}
                                onChange={(_, value) => handleKeySelect(idx, value?.id || null)}
                                renderInput={(params) => (
                                  <TextField 
                                    {...params} 
                                    label="Select API Key" 
                                    size="small"
                                    placeholder="Choose a key for this tool"
                                    required
                                  />
                                )}
                                renderOption={(props, option: any) => (
                                  <li {...props}>
                                    <Box>
                                      <Typography variant="body2">{option.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {option.provider} • {option.key_type.replace('_', ' ')}
                                      </Typography>
                                    </Box>
                                  </li>
                                )}
                                sx={{ maxWidth: 400 }}
                              />
                            </Box>
                          )}

                          {tool.params && tool.params.length > 0 && (
                            <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Parameters:
                              </Typography>
                              {tool.params.map((param: any, paramIdx: any) => (
                                <Typography key={paramIdx} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  • {param.name} <em>({param.type})</em>{param.required && <strong> *required</strong>}
                                  {param.description && ` - ${param.description}`}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        // Editable display for custom tools
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-end",
                              mb: 1,
                              gap: 2,
                            }}
                          >
                            <Autocomplete
                              freeSolo
                              options={[]}
                              value={tool.name}
                              onInputChange={(_, value) =>
                                handleEditTool(idx, "name", value)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Tool Name"
                                  size="small"
                                />
                              )}
                              sx={{ width: "22%" }}
                            />
                            <Autocomplete
                              freeSolo
                              options={[]}
                              value={tool.description}
                              onInputChange={(_, value) =>
                                handleEditTool(idx, "description", value)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Description"
                                  size="small"
                                />
                              )}
                              sx={{ width: "45%" }}
                            />
                            {/* Invocation Type dropdown for each selected tool */}
                            <Box sx={{ width: "20%", mt: 2 }}>
                              <Autocomplete
                                options={[
                                  "python_function",
                                  "http_request",
                                  "javascript_function",
                                ]}
                                value={tool.invocation?.type || "python_function"}
                                onChange={(_, value) =>
                                  handleInvocationTypeChange(idx, value)
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Invocation Type"
                                    size="small"
                                  />
                                )}
                                disableClearable
                              />
                            </Box>
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              onClick={() => handleToolRemove(tool.name)}
                            >
                              Remove
                            </Button>
                          </Box>
                          
                          {/* Key Selection for Custom Tools */}
                          <Box sx={{ mb: 2, mt: 1 }}>
                            <Autocomplete
                              options={[]}
                              getOptionLabel={(option: any) => `${option.name} (${option.provider})`}
                              // value={availableKeys.find((k: any) => k.id === tool.selectedKey) || null}
                              value={null}
                              onChange={(_, value) => handleKeySelect(idx, value?.id || null)}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  label="Select API Key" 
                                  size="small"
                                  placeholder="Choose a key for this tool"
                                />
                              )}
                              renderOption={(props, option: any) => (
                                <li {...props}>
                                  <Box>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.provider} • {option.key_type.replace('_', ' ')}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              sx={{ maxWidth: 400 }}
                            />
                          </Box>

                          {tool.params && tool.params.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                Parameters:
                              </Typography>
                              {tool.params.map((param: any, paramIdx: any) => (
                                <Box
                                  key={paramIdx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 1,
                                  }}
                                >
                                  <TextField
                                    label="Name"
                                    value={param.name}
                                    onChange={(e) =>
                                      handleEditParam(
                                        idx,
                                        paramIdx,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{ mr: 1, width: "25%" }}
                                  />
                                  <Autocomplete
                                    options={["string", "number", "boolean", "json"]}
                                    value={param.type}
                                    onChange={(_, value) =>
                                      handleEditParam(idx, paramIdx, "type", value)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Type"
                                        size="small"
                                      />
                                    )}
                                    sx={{ mr: 1, width: "20%" }}
                                  />
                                  <TextField
                                    label="Description"
                                    value={param.description}
                                    onChange={(e) =>
                                      handleEditParam(
                                        idx,
                                        paramIdx,
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{ mr: 1, width: "45%" }}
                                  />
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveParam(idx, paramIdx)}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            
            {/* Validation Message for API Keys */}
            {selectedTools.length > 0 && !isCurrentStepComplete() && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Please select an API key for all tools before continuing to the next step.
                </Typography>
              </Alert>
            )}
            
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            {/* ****************************************************** */}

            {/* Tool Detail Modal */}
            <Dialog 
              open={toolDetailOpen} 
              onClose={() => setToolDetailOpen(false)}
              maxWidth="md"
              fullWidth
            >
              {selectedToolDetail && (
                <>
                  <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" component="span" sx={{ flex: 1 }}>
                        {selectedToolDetail.name}
                      </Typography>
                      <Chip 
                        label={`v${selectedToolDetail.version}`} 
                        size="small" 
                      />
                    </Box>
                  </DialogTitle>
                  <DialogContent dividers>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {selectedToolDetail.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip 
                          label={selectedToolDetail.category || 'Other'} 
                          color="primary"
                          size="small"
                        />
                        <Chip 
                          label={selectedToolDetail.invocation_type || 'python_function'} 
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Box>

                    {selectedToolDetail.params && selectedToolDetail.params.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Parameters ({selectedToolDetail.params.length})
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                          {selectedToolDetail.params.map((param: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {param.name}
                                </Typography>
                                <Chip 
                                  label={param.type} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                                {param.required && (
                                  <Chip 
                                    label="required" 
                                    size="small" 
                                    color="error"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                              {param.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {param.description}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {selectedToolDetail.return_value && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Return Value
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'success.main' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip 
                              label={selectedToolDetail.return_value.type || 'any'} 
                              size="small" 
                              color="success"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                          {selectedToolDetail.return_value.description && (
                            <Typography variant="caption" color="text.secondary">
                              {selectedToolDetail.return_value.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setToolDetailOpen(false)}>
                      Close
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={() => {
                        handleToolSelect(selectedToolDetail);
                        setToolDetailOpen(false);
                      }}
                      disabled={selectedTools.some((t: any) => t.tool_id === selectedToolDetail.tool_id)}
                    >
                      {selectedTools.some((t: any) => t.tool_id === selectedToolDetail.tool_id) 
                        ? '✓ Already Added' 
                        : 'Add to Agent'}
                    </Button>
                  </DialogActions>
                </>
              )}
            </Dialog>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            ></Box>
          </Box>
        );

      // Step 3: LLM settings
      case 3:
        // Sync manual JSON and form values
        const handleManualSwitch = (checked: any) => {
          setManualMode(checked);
          if (checked) {
            // Populate manual JSON from form
            setManualJsonValue(
              JSON.stringify(
                {
                  llm: form.llm,
                  temperature: form.temperature,
                  max_tokens: form.max_tokens,
                },
                null,
                2
              )
            );
          } else {
            // Populate form from manual JSON
            try {
              const json = JSON.parse(manualJsonValue);
              setForm((f) => ({
                ...f,
                llm: json.llm || "openai",
                temperature:
                  typeof json.temperature === "number" ? json.temperature : 0,
                max_tokens:
                  typeof json.max_tokens === "number" ? json.max_tokens : 4096,
              }));
            } catch (e) {
              // Ignore parse errors
            }
          }
        };
        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role: <b>{form.role}</b>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intent: <b>{form.name}</b>
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={manualMode}
                    onChange={(e) => handleManualSwitch(e.target.checked)}
                  />
                }
                label="Manual JSON mode"
              />
            </Box>
            {!manualMode ? (
              <Box>
                <Autocomplete
                  options={llmOptions}
                  value={form.llm}
                  onChange={(_, value) =>
                    setForm((f) => ({ ...f, llm: value || "openai" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="LLM Provider" />
                  )}
                  sx={{ minWidth: 220, mb: 2 }}
                  disableClearable
                />
                <TextField
                  label="Temperature"
                  type="number"
                  value={form.temperature}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      temperature: parseFloat(e.target.value),
                    }))
                  }
                  sx={{ mb: 2 }}
                  fullWidth
                />
                <TextField
                  label="Max Tokens"
                  type="number"
                  value={form.max_tokens}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      max_tokens: parseInt(e.target.value),
                    }))
                  }
                  sx={{ mb: 2 }}
                  fullWidth
                />
              </Box>
            ) : (
              <TextField
                label="LLM Settings JSON"
                multiline
                minRows={8}
                value={manualJsonValue}
                onChange={(e) => setManualJsonValue(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            ></Box>
          </Box>
        );

      case 4:
        return (
          <Box>
            {!isEdit &&
              <Alert severity="success" sx={{ mb: 3 }}>
                
                <Typography variant="body2">
                  <strong>You're all set!</strong> Review your configuration
                  below. You can always edit these settings later.
                </Typography>
              </Alert>
            }
            <Box
              // component={Paper}
              // elevation={3}
              sx={{ maxWidth: 1080, mx: "auto", my: 2, p: 1 }}
            >
              {/* <Typography variant="h5" gutterBottom>{isEdit ? "Review & Update Agent" : "Review & Create Agent"}</Typography> */}
              <Box sx={{ mb: 2 }}>
                {isEdit && (
                  <Typography variant="body2" color="text.secondary">
                    Agent ID: <b>{editingAgentCard.agent_id}</b>
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Role: <b>{form.role}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Intent: <b>{form.name}</b>
                </Typography>
              </Box>
              {/* Agent Description */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Agent Description
                </Typography>
                <Typography variant="body2">{form.description}</Typography>
              </Box>
              {/* Tools Review */}
              {reviewTools.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    Selected Tools
                  </Typography>
                  {reviewTools.map((tool: any) => (
                    <Box
                      key={tool.name}
                      sx={{ mb: 1, pl: 2, borderLeft: "2px solid #eee" }}
                    >
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {tool.name} - {tool.description}
                      </Typography>
                      {tool.parameters && tool.parameters.length > 0 && (
                        <Box sx={{ pl: 2 }}>
                          {tool.parameters.map((param: any) => (
                            <Typography
                              key={param.name}
                              variant="caption"
                              color="text.secondary"
                            >
                              {param.name} ({param.type}): {param.description}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {/* LLM Settings Review */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  LLM Settings
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Provider: <b>{form.llm}</b>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Temperature: <b>{form.temperature}</b>
                </Typography>
                <Typography variant="body2">
                  Max Tokens: <b>{form.max_tokens}</b>
                </Typography>
              </Box>
              {/* Associate Indexes */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Knowledge Collection (optional)
                </Typography>
                <Autocomplete
                  options={docIndexes}
                  getOptionLabel={(opt: any) => opt.name || opt.index_id}
                  value={
                    docIndexes.find(
                      (i: any) => i.index_id === selectedDocIndex
                    ) || null
                  }
                  onChange={(_, value) =>
                    setSelectedDocIndex(value ? value.index_id : null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Document Collection" />
                  )}
                  sx={{ mb: 2 }}
                />
                
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };
  
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Page Header - Fixed */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h5" fontWeight="600" sx={{ mb: 0.5 }}>
          {isEdit ? "Edit Agent" : "Create Agent"}
        </Typography>
        {!isEdit && 
        <Typography variant="body2" color="text.secondary">
          Follow the steps below to configure your new agent
        </Typography>
  }
      </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ flex: 1, overflow: "auto", px: 3 }}>
        <Paper
          elevation={2}
          sx={{ pb: 3, pl: 3, pr: 3, pt: 1, mb: 3, maxWidth: 1080 }}
        >
          {/* Stepper - Compact */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((step, index) => {
              // For non-admin in edit mode, mark steps 0-3 as completed and disabled
              // For admin or create mode, use the normal completedSteps state
              const isStepDisabled = isEdit && isNonAdmin && index < 4;
              const isStepCompleted = isEdit && isNonAdmin && index < 4 ? true : completedSteps[index];
              
              return (
                <Step key={step.label} completed={isStepCompleted} disabled={isStepDisabled}>
                  <StepLabel 
                    sx={{
                      ...(isStepDisabled && {
                        '& .MuiStepLabel-label': {
                          color: 'text.disabled',
                          opacity: 0.6
                        },
                        '& .MuiStepIcon-root': {
                          color: 'action.disabled',
                          opacity: 0.6
                        }
                      })
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Step Header with Guidance - Compact */}
          <Box sx={{ mb: 0.5 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="h6" fontWeight="600">
                {currentStepConfig.title}
              </Typography>
              {currentStepConfig.helpText && (
                <Tooltip title={currentStepConfig.helpText} arrow>
                  <IconButton size="small" color="primary">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {currentStepConfig.description || ""}
            </Typography>
            {currentStepConfig.tipText && (
              <Alert severity="info" icon={false} sx={{ pt: 0.5 }}>
                <Typography variant="body2">
                  {currentStepConfig.tipText}
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Step Content - Scrollable if needed */}
          <Box sx={{ mb: 2 }}>{renderStepContent()}</Box>

          {/* Navigation - Fixed at bottom of card */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0 || (isEdit && isNonAdmin)}
              startIcon={<NavigateBeforeIcon />}
              sx={{ borderRadius: "99px" }}
            >
              Back
            </Button>
            {/* Confirmation & Create/Update Button */}
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateAgent}
                disabled={loading}
                sx={{ width: "100%", maxWidth: 300 }}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : isEdit ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<NavigateNextIcon />}
                disabled={!isCurrentStepComplete()} // ← Disabled based on validation
                sx={{ borderRadius: "99px" }}
              >
                Continue
              </Button>
            )}
          </Box>
          {successMsg && (
            <Box sx={{ mt: 2 }}>
              <Typography color="success.main" sx={{ mb: 1 }}>
                <span dangerouslySetInnerHTML={{ __html: successMsg }} />
              </Typography>
            </Box>
          )}
          {success && (
            <Box sx={{ mt: 3 }}>
              <Typography color="success.main" sx={{ mb: 1 }}>
                <span dangerouslySetInnerHTML={{ __html: success }} />
              </Typography>
            </Box>
          )}
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StepperWizard;
